import {TrustPurpose} from '@sollapay/enums';

import {defaultTrustType} from './default.trust-type';
import {getGeneralSaleTrustType} from './general-sale.trust-type';
import {optionsTrustType} from './options.trust-type';
import {sevenPercentTrustType} from './seven-percent.trust-type';
import {TrustTypeStrategyNotFoundError} from '../errors';

import type {TrustTypeStrategy} from './trust-type.strategy';
import type {GeneralSaleTransactionType} from '@sollapay/enums';

type TrustTypeResolver = (transactionType: GeneralSaleTransactionType | null) => TrustTypeStrategy;

/**
 * Registry of trust type strategies, keyed by `TrustPurpose`.
 *
 * Adding a new trust type: implement `TrustTypeStrategy` in its own file and add one
 * entry here — the exhaustive `Record` makes a missing entry a compile error. Entries are
 * resolvers so a purpose may narrow further on a sub-discriminator.
 */
const trustTypeStrategies: Record<TrustPurpose, TrustTypeResolver> = {
  [TrustPurpose.SEVEN_PERCENT]: () => sevenPercentTrustType,
  [TrustPurpose.GENERAL_SALE]: getGeneralSaleTrustType,
  [TrustPurpose.OPTIONS]: () => optionsTrustType
};

/**
 * Everything that selects a strategy. Both keys are required so a caller can't silently drop the
 * transaction type and land on second-hand for an option-assignment account — a trust account
 * entity or DTO satisfies it as-is; pass `generalSaleTransactionType: null` only when it is
 * genuinely unknown.
 */
export interface TrustTypeKey {
  trustPurpose: TrustPurpose | null;
  generalSaleTransactionType: GeneralSaleTransactionType | null;
}

/** For a trust account that could not be loaded — resolves to the conservative default strategy. */
export const NO_TRUST_TYPE: TrustTypeKey = Object.freeze({
  trustPurpose: null,
  generalSaleTransactionType: null
});

/**
 * Gets the strategy for a trust type. A `null` purpose resolves to the conservative default
 * strategy rather than being scattered as `?? default` at call sites. The transaction type only
 * narrows general sale.
 *
 * @example
 * getTrustTypeStrategy(trustAccount).maxCollectionPercentage
 * getTrustTypeStrategy({trustPurpose: TrustPurpose.SEVEN_PERCENT, generalSaleTransactionType: null})
 */
export function getTrustTypeStrategy({
  trustPurpose,
  generalSaleTransactionType
}: TrustTypeKey): TrustTypeStrategy {
  if (!trustPurpose) {
    return defaultTrustType;
  }
  const resolve = trustTypeStrategies[trustPurpose];
  if (!resolve) {
    throw new TrustTypeStrategyNotFoundError(trustPurpose);
  }
  return resolve(generalSaleTransactionType);
}
